import { FC, useCallback, useContext, useState, forwardRef } from 'react';

import {
  CheckIcon,
  DuplicateIcon,
  PencilIcon,
  TrashIcon,
  XIcon,
  EyeIcon,
} from '@heroicons/react/outline';

import { capitalize, IconButton } from '@mui/material';
import { GridRenderCellParams, GridRowId, GridRowModel, GridRowModes, useGridApiContext } from '@mui/x-data-grid-pro';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';

import { newGridRecordId } from '@shared/commonFunctions';
import { GridRowContext } from '@shared/components/grid/PhoenixGrid';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { GridColActionsWrapper } from '@styles/PhoenixGrid/GridStyles';
import { colorTheme } from '@styles/muiTheme';

interface IGridColActionsProps {
  name?: string;
  canNotCopy?: boolean;
  canNotEdit?: boolean;
  params: GridRenderCellParams;
  isRowValid: boolean;
  pageSize: number;
  setEditableRowId: (rowId: GridRowId | null) => void;
  editableRowId: GridRowId | null;
  withoutConfirmation?: boolean;
  ref: React.MutableRefObject<GridApiPro>;
  rowActionsSlot?: FC<GridRowModel>;
  onSaveClick?: () => void;
  onCopyClick?: (row: GridRowModel) => void;
  onDeleteClick?: (row: GridRowModel) => void;
  onEditClick?: (row: GridRowModel) => void;
  onCancelClick?: (row: GridRowModel) => void;
  onViewClick?: (row: GridRowModel) => void; 
}

export const gridRowActionsIconProps = {
  color: `${colorTheme.palette.primary.light}`,
  width: 18,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const navigateToLastGridRow = (api: any, pageSize: number) => {
  setTimeout(() => {
    const ids = api.getSortedRowIds();
    const newRowIndex = ids.indexOf(newGridRecordId);
    const lastPage = Math.floor(newRowIndex / pageSize);
    api.setPage(lastPage);
    api.scrollToIndexes({
      rowIndex: newRowIndex,
    });
  });
};

export const GridColActions = forwardRef(
  (
    {
      name = 'item',
      canNotCopy,
      canNotEdit,
      params,
      onSaveClick,
      onCopyClick,
      onEditClick,
      onDeleteClick,
      onCancelClick,
      onViewClick,
      pageSize,
      withoutConfirmation,
      rowActionsSlot: RowActionsSlot,
      setEditableRowId,
      editableRowId,
    }: IGridColActionsProps,
    _ref,
  ) => {
    const api = useGridApiContext();
    const rowContext = useContext(GridRowContext);
    const { id, row } = params;

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isEditMode =
      api.current.getRowMode(id) === GridRowModes.Edit;


    const handleEditClick = useCallback(
      async(event: React.MouseEvent) => {
        event.stopPropagation();
        if (editableRowId !== null) {
          const editableRowMode = api.current.getRowMode(editableRowId) === GridRowModes.Edit;
          editableRowId === newGridRecordId && api.current.updateRows([{id: editableRowId, _action: 'delete'}]);
          editableRowMode && (await api.current.stopRowEditMode({ id: editableRowId, ignoreModifications: true }));
          rowContext.resetErrors();
        }

        if (onEditClick) {
          onEditClick(row);
        } else {
          await api.current.startRowEditMode({ id });
        }

        setEditableRowId(id);
      },
      [row, id, editableRowId, setEditableRowId],
    );

    
    const handleViewClick = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();

        if (editableRowId !== null) {
          const editableRowMode =
            api.current.getRowMode(editableRowId) === GridRowModes.Edit;

          editableRowMode &&
            api.current.stopRowEditMode({
              id: editableRowId,
              ignoreModifications: true,
            });

          rowContext.resetErrors();
        }

        onViewClick?.(row);
      },
      [api, editableRowId, row, rowContext, onViewClick],
    );

    const handleSaveClick = useCallback(
      (event: React.SyntheticEvent) => {
        event.stopPropagation();
        onSaveClick?.();
    }, [onSaveClick]);

    const handleCancelClick = useCallback((event: React.SyntheticEvent) => {
        event.stopPropagation();

        rowContext.resetErrors();

        setEditableRowId(null);

        if (id === newGridRecordId || row.isNew) {
          api.current.updateRows([{ id, _action: 'delete' }]);
        }

      api.current.stopRowEditMode({ id, ignoreModifications: true });

        onCancelClick?.(row);
    }, [rowContext, api.current, id, row, onCancelClick]);

    const handleDuplicateClick = useCallback(async(event: React.MouseEvent) => {
        event.stopPropagation();

        if (editableRowId !== null) {
        const editableRowMode = api.current.getRowMode(editableRowId) === GridRowModes.Edit;
        editableRowMode && (await api.current.stopRowEditMode({ id: editableRowId, ignoreModifications: true }));
          rowContext.resetErrors();
        }

        if (onCopyClick) {
          onCopyClick?.(row);
        } else {
          const id = newGridRecordId;
        const updatedRow = [{ ...row, id, _id: row.id, isNew: true }];
          await api.current.updateRows(updatedRow);
          await api.current.startRowEditMode({ id });
        }

        navigateToLastGridRow(api.current, pageSize);

      setEditableRowId(id);
    }, [api.current, pageSize, row, isEditMode, editableRowId]);

    const handleDeleteClick = (event?: React.MouseEvent) => {
      event?.stopPropagation();
      api.current.updateRows([{ ...row, id, _action: 'delete' }]);

      onDeleteClick?.(row);
    };

    return (
      <GridColActionsWrapper>
        {isEditMode ? (
          <>
            <IconButton
              onClick={handleSaveClick}
              onKeyUp={(event) => event.key === 'Enter' && handleSaveClick(event)}
            >
              <CheckIcon {...gridRowActionsIconProps} />
            </IconButton>
            <IconButton
              onClick={handleCancelClick}
              onKeyUp={(event) => event.key === 'Enter' && handleCancelClick(event)}
            >
              <XIcon {...gridRowActionsIconProps} />
            </IconButton>
          </>
        ) : (
          <>
            {!canNotEdit && (
              <IconButton data-action="edit-row" tabIndex={2} onClick={handleEditClick}>               
                <PencilIcon {...gridRowActionsIconProps} />
              </IconButton>
            )}

            
            {onViewClick && (
              <IconButton tabIndex={2} onClick={handleViewClick}>
                <EyeIcon {...gridRowActionsIconProps} />
              </IconButton>
            )}

           
            {!canNotCopy && (
              <IconButton disabled={isEditMode} tabIndex={2} onClick={handleDuplicateClick}>
                <DuplicateIcon {...gridRowActionsIconProps} />
              </IconButton>
            )}

           
            {(row.showDeleteButton || onDeleteClick) && (
              <IconButton tabIndex={2} 
                  onClick={(event) => {
                  event.stopPropagation();
                  withoutConfirmation ? handleDeleteClick(event) : setShowDeleteModal(true);
                }}
                >
                <TrashIcon {...gridRowActionsIconProps} />
              </IconButton>
            )}

            {RowActionsSlot && <RowActionsSlot {...params} />}

            {showDeleteModal && (
              <AlertModal
                title={`Delete ${capitalize(name)}`}
                subtitle={`Are you sure you want to delete this ${name}?`}
                open={showDeleteModal}
                onConfirm={handleDeleteClick}
                onClose={() => setShowDeleteModal(false)}
              />
            )}
          </>
        )}
      </GridColActionsWrapper>
    );
  },
);
